import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell } from "@/components/page-shell";
import {
  ArticleBody,
  Clause,
  clauseToc,
  DotItem,
  DotList,
  EffectiveDate,
} from "@/components/guide-page";
import { absUrl } from "@/lib/site";

const EFFECTIVE_DATE = "July 20, 2026";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms of Service | LoneStarMap" },
      {
        name: "description",
        content:
          "The terms that govern use of LoneStarMap.com, an educational, ad-free site about ticks and alpha-gal awareness.",
      },
      { property: "og:title", content: "Terms of Service" },
      {
        property: "og:description",
        content: "Terms governing use of LoneStarMap.com.",
      },
      { property: "og:url", content: absUrl("/terms") },
    ],
    links: [{ rel: "canonical", href: absUrl("/terms") }],
  }),
  component: TermsPage,
});

const TITLES = [
  "Eligibility",
  "Educational purpose, not medical advice",
  "Content you submit",
  "Prohibited uses",
  "Intellectual property",
  "Third-party links and services",
  "Copyright / DMCA",
  'Disclaimer of warranties ("as is")',
  "Limitation of liability",
  "Indemnification",
  "Governing law and venue",
  "Changes to these Terms",
  "Severability and entire agreement",
  "Contact",
] as const;

const LIST = "text-base leading-[1.7] text-foreground/85";

function TermsPage() {
  return (
    <PageShell
      title="Terms of Service"
      intro='These Terms of Service ("Terms") govern your access to and use of LoneStarMap.com and any related pages, tools, or content (collectively, the "Site"). By using the Site, you agree to these Terms. If you do not agree, do not use the Site.'
    >
      <EffectiveDate date={EFFECTIVE_DATE} />

      <ArticleBody toc={clauseToc(TITLES)}>
        <Clause titles={TITLES} n={1}>
          <p>
            The Site is intended for adults 18 years or older. If you are under 18, use the Site
            only with the involvement and consent of a parent or legal guardian. Do not submit tick
            reports or contact messages if you are under 18.
          </p>
        </Clause>

        <Clause titles={TITLES} n={2}>
          <p>
            The Site provides general educational information about ticks, tick-bite prevention, and
            alpha-gal syndrome awareness. It does <strong>not</strong> provide medical advice,
            diagnosis, or treatment, and no doctor–patient relationship is created by your use of
            the Site. Always consult a licensed healthcare professional for medical questions. In an
            emergency, call 911 (or your local emergency number) or contact Poison Control.
          </p>
        </Clause>

        <Clause titles={TITLES} n={3}>
          <p>
            The contact form lets you submit content. By submitting content
            you represent that: (a) it is accurate to the best of your knowledge; (b) it does not
            contain personal information about other people (names, addresses, phone numbers, or
            accusations about specific individuals or properties); (c) it does not contain unlawful,
            defamatory, harassing, or infringing material; and (d) you have the right to submit it.
          </p>
          <p>
            You grant LoneStarMap a worldwide, royalty-free, non-exclusive, perpetual license to
            host, store, reproduce, adapt, aggregate, and display your submissions in connection
            with operating the Site and related educational materials. You retain ownership of your
            submissions. We may remove any submission at any time for any reason, including
            suspected misuse.
          </p>
        </Clause>

        <Clause titles={TITLES} n={4}>
          <p>You agree not to:</p>
          <DotList className={LIST}>
            <DotItem>
              Use the Site for any unlawful purpose or in violation of any applicable law.
            </DotItem>
            <DotItem>Submit false, misleading, defamatory, or harassing content.</DotItem>
            <DotItem>
              Scrape, crawl, or copy the Site at a rate that burdens our infrastructure.
            </DotItem>
            <DotItem>Attempt to gain unauthorized access to any account, system, or data.</DotItem>
            <DotItem>Present Site content as official medical or public-health guidance.</DotItem>
          </DotList>
        </Clause>

        <Clause titles={TITLES} n={5}>
          <p>
            Except for user submissions and third-party content, the Site's content, design, and
            code are owned by LoneStarMap and its licensors and are protected by
            intellectual-property laws. You may share links to the Site freely; you may not
            republish substantial portions of the content without permission.
          </p>
        </Clause>

        <Clause titles={TITLES} n={6}>
          <p>
            The Site links to third-party resources (public-health agencies, research, tools). We do
            not control and are not responsible for third-party content or practices. Following an
            external link is at your own risk.
          </p>
        </Clause>

        <Clause titles={TITLES} n={7}>
          <p>
            If you believe content on the Site infringes your copyright, please send a notice
            through our{" "}
            <Link to="/contact" className="link">
              contact form
            </Link>{" "}
            identifying the work, the URL, and your contact information. We will review and respond
            in accordance with applicable law.
          </p>
        </Clause>

        <Clause titles={TITLES} n={8}>
          <p>
            The Site is provided <strong>"as is"</strong> and <strong>"as available"</strong>{" "}
            without warranties of any kind, express or implied, including merchantability, fitness
            for a particular purpose, non-infringement, or accuracy. We do not warrant that the Site
            will be uninterrupted, error-free, secure, or that any information is complete or
            current.
          </p>
        </Clause>

        <Clause titles={TITLES} n={9}>
          <p>
            To the fullest extent permitted by law, LoneStarMap, its operators, and its contributors
            are not liable for any indirect, incidental, special, consequential, exemplary, or
            punitive damages, or any loss of profits, revenues, data, or health outcomes arising
            from or related to your use of the Site, even if we have been advised of the
            possibility. Our total aggregate liability for any claim relating to the Site will not
            exceed one hundred U.S. dollars (US$100).
          </p>
        </Clause>

        <Clause titles={TITLES} n={10}>
          <p>
            You agree to defend, indemnify, and hold harmless LoneStarMap and its operators from and
            against any claims, damages, liabilities, and expenses (including reasonable attorney's
            fees) arising from your use of the Site, your submissions, or your violation of these
            Terms.
          </p>
        </Clause>

        <Clause titles={TITLES} n={11}>
          <p>
            These Terms are governed by the laws of the State of Texas, without regard to
            conflict-of-laws principles. You agree that the exclusive venue for any dispute arising
            from or relating to these Terms or the Site shall be the state or federal courts located
            in Texas, and you consent to personal jurisdiction there.
          </p>
        </Clause>

        <Clause titles={TITLES} n={12}>
          <p>
            We may update these Terms from time to time. Material changes will be reflected in a new
            effective date at the top of this page. Your continued use of the Site after changes
            take effect constitutes acceptance of the updated Terms.
          </p>
        </Clause>

        <Clause titles={TITLES} n={13}>
          <p>
            If any provision of these Terms is held unenforceable, the remaining provisions will
            remain in full force and effect. These Terms, together with our{" "}
            <Link to="/privacy" className="link">
              Privacy Policy
            </Link>{" "}
            and{" "}
            <Link to="/disclaimer" className="link">
              Disclaimer
            </Link>
            , constitute the entire agreement between you and LoneStarMap regarding the Site.
          </p>
        </Clause>

        <Clause titles={TITLES} n={14}>
          <p>
            Questions about these Terms? Send a note through our{" "}
            <Link to="/contact" className="link">
              contact form
            </Link>
            .
          </p>
        </Clause>
      </ArticleBody>
    </PageShell>
  );
}
